import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import MarketplacePanel from "@/components/shop/MarketplacePanel";
import { CheckCircle2, Clock, Gift, ArrowRight, Loader2 } from "lucide-react";

export default function ZoneClaim() {
  const navigate = useNavigate();
  const [claim, setClaim] = useState(undefined); // undefined = loading

  const refresh = () => api.get("/shop/founding-claim").then(({ data }) => setClaim(data)).catch(() => setClaim({ eligible: true, claim: null }));
  useEffect(() => { refresh(); }, []);

  const pending = claim?.claim && ["pending", "open", "approved", "discussion"].includes(claim.claim.status);
  const fulfilled = claim?.claim?.status === "fulfilled";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="font-heading font-extrabold tracking-tight">VELO<span className="text-primary">PATHS</span></div>
          <Button variant="ghost" onClick={() => navigate("/shop")} data-testid="claim-skip-dashboard">Continue to dashboard <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold" data-testid="claim-headline">Claim Your Complimentary Founding Partner Zone</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">As a Founding Partner, your first zone is included at no additional cost for one year. Browse available zones below and select the territory you'd like to manage.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="claim-benefits">
            <div className="flex items-center gap-2 mb-4"><Gift className="w-5 h-5 text-emerald-400" /><h2 className="font-heading text-lg font-bold">Your Founding Partner Benefits</h2></div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {["One complimentary zone included for your first year", "Full access to territory management tools", "Manage routes, stops, businesses, and advertisers", "Additional zones may be leased through the Partner Portal at any time"].map((x) => (
                <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{x}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6" data-testid="claim-rules">
            <h2 className="font-heading text-lg font-bold mb-4">Zone Claim Rules</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>Each Founding Partner may claim one complimentary zone</li>
              <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>Zone claims are reviewed prior to activation</li>
              <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>Additional zones may be leased separately</li>
            </ul>
          </div>
        </div>

        {claim === undefined ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : pending ? (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-8 text-center" data-testid="claim-pending">
              <Clock className="w-12 h-12 text-amber-400 mx-auto mb-3" />
              <h2 className="font-heading text-2xl font-bold">Zone Claim Submitted</h2>
              <p className="text-muted-foreground mt-2">Your request has been received and is currently under review.</p>
              <div className="mt-4 text-sm"><span className="text-muted-foreground">Zone:</span> <strong>{claim.claim.zoneName || "—"}</strong></div>
              <div className="text-sm"><span className="text-muted-foreground">Status:</span> <strong className="text-amber-400">{claim.claim.status === "discussion" ? "Discussion requested" : "Pending Approval"}</strong></div>
              {claim.claim.adminNote && <p className="text-sm mt-2 italic">"{claim.claim.adminNote}"</p>}
              <p className="text-sm text-muted-foreground mt-4">You may continue setting up your profile while we review your claim.</p>
              <Button className="mt-5" onClick={() => navigate("/shop")} data-testid="claim-continue-setup">Continue setting up your profile</Button>
            </div>
          ) : fulfilled ? (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8 text-center" data-testid="claim-fulfilled">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <h2 className="font-heading text-2xl font-bold">Your zone is active</h2>
              <p className="text-muted-foreground mt-2"><strong>{claim.claim.zoneName}</strong> is now yours to manage.</p>
              <Button className="mt-5" onClick={() => navigate("/shop")} data-testid="claim-go-dashboard">Go to your dashboard</Button>
            </div>
          ) : (
            <MarketplacePanel claimMode onClaimed={refresh} />
          )}
      </main>
    </div>
  );
}
